import axios from "axios";
import toast from "react-hot-toast";

/**
 * Primary axios instance. baseURL is read from env so that local dev,
 * staging, and production each use their own backend without code changes.
 *
 * Request interceptors (Authorization header + auto-refresh on 401) are
 * registered by AuthProvider at mount time — see src/app/providers/AuthProvider.js.
 */
const apiClient = axios.create({
  // Empty string → relative URLs (/api/...) handled by src/setupProxy.js in dev,
  // so the API is same-origin and the refresh cookie stays first-party.
  baseURL: process.env.REACT_APP_API_BASE_URL || "",
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // allows the browser to send the HttpOnly refresh cookie
  // Safety net: never leave a request pending forever (infinite spinner). A
  // stalled call rejects after 30s so the UI can show an error state instead.
  timeout: 30000,
});

// ── 402 Plan Gate interceptor (TASK-18) ──────────────────────────────────────
// Registered at module load time (before AuthProvider's 401 interceptor).
// When any endpoint returns 402 with suggest_upgrade=true, dispatches a global
// CustomEvent so PlanGateProvider (src/app/providers/PlanGateProvider.js) can
// render the UpgradeNudge banner without each feature handling 402 itself.
//
// The error is always re-thrown so React Query / callers still receive it for
// inline error handling. The event is a side-effect, not a replacement.
//
// TASK-19: wire <PlanGateProvider> into src/App.js provider tree to activate
// the global banner. Until then, inline suggest_upgrade checks in PlanPage cover
// the owner's own plan flow.
apiClient.interceptors.response.use(null, (error) => {
  if (error.response?.status === 402 && error.response.data?.suggest_upgrade) {
    window.dispatchEvent(
      new CustomEvent("plan-gate-error", { detail: error.response.data })
    );
  }
  // Academia congelada por falta de pago (TASK-20): un toast global avisa a
  // docentes/estudiantes que intentan escribir. El id evita duplicados; si no
  // hay <Toaster> en la página montado, la llamada es un no-op inofensivo.
  if (
    error.response?.status === 403 &&
    error.response.data?.code === "subscription_frozen"
  ) {
    toast.error(
      error.response.data.detail ||
        "La suscripción de tu academia está congelada por falta de pago.",
      { id: "subscription-frozen" }
    );
  }
  // Funcionalidad no incluida en el plan (TASK-20, Fase 3): honorarios, etc.
  if (
    error.response?.status === 403 &&
    error.response.data?.code === "feature_not_available"
  ) {
    toast.error(
      error.response.data.detail ||
        "Esta funcionalidad no está incluida en tu plan.",
      { id: "feature-not-available" }
    );
  }
  return Promise.reject(error);
});

export { apiClient };
